import React from "react";
import colors, { ListColor } from "./colors";
import styled, { css } from "styled-components";
import useTheme from "../../lib/useTheme";
import { mergeStrings } from "../../lib/helpers";

interface DGA_LinkProps extends Omit<React.OlHTMLAttributes<HTMLOListElement>, "style" | "type"> {
  style?: "primary" | "neutral" | "on-color";
  type?: "ordered-list" | "unordered-list" | "with-icon";
  level?: 1 | 2 | "1" | "2";
  iconUrl?: string;
  children: React.ReactNode;
}

const List: React.FC<DGA_LinkProps> = ({
  children,
  style = "primary",
  type = "ordered-list",
  level = 1,
  iconUrl = "",
  ...props
}) => {
  const theme = useTheme();
  const selectedColors = colors(theme)[style];

  const Result = type === "ordered-list" ? StyledOlComponent : StyledUlComponent;

  return (
    <Result
      $theme={theme}
      $colors={selectedColors}
      $listStyleType={getListStyleType(type, level)}
      {...props}
      className={mergeStrings("dgaui dgaui_list", props.className)}
      type={type === "ordered-list" && +level === 1 ? "1" : "a"}
      $iconUrl={iconUrl}
      $level={+level}
      $ol={type === "ordered-list"}
    >
      {children}
    </Result>
  );
};

const StyledUlComponent = styled.ul<{
  $theme: Theme;
  $colors: ListColor;
  $listStyleType?: string;
  $iconUrl?: string;
  $level?: number;
}>`
  ${(p) => getCss(p)};
`;

const StyledOlComponent = styled.ol<{
  $theme: Theme;
  $colors: ListColor;
  $listStyleType?: string;
  $iconUrl?: string;
  $level?: number;
  $ol: boolean;
}>`
  ${(p) => getCss(p)};
`;

export default List;

const getCss = (p: {
  $theme: Theme;
  $colors: ListColor;
  $listStyleType?: string;
  $iconUrl?: string;
  $level?: number;
  $ol?: boolean;
}) => {
  return css`
    direction: ${p.$theme.direction};
    color: ${p.$colors.text};
    font-size: 16px;
    padding-inline-start: ${p.$level === 1 ? 0 : ""};

    ${p.$listStyleType ? "list-style-type: " + p.$listStyleType : ""};

    ${p.$listStyleType === "decimal" && p.$level === 1 && p.$ol
      ? css`
          list-style-type: none; /* Remove default numbering */
          counter-reset: item; /* Initialize the counter */

          li {
            display: flex;
            align-items: center;
            position: relative;

            &::before {
              content: counter(item) "- ";
              counter-increment: item;

              height: 16px;
              width: 16px;
              position: absolute;
              top: 0;
              inset-inline-start: -24px;
            }
            &::marker {
              content: "";
            }
          }
        `
      : ""};

    ${p.$listStyleType === "with-icon" && p.$iconUrl
      ? css`
          li {
            display: flex;
            align-items: center;
            position: relative;

            &::before {
              content: url(${p.$iconUrl});
              height: 16px;
              width: 16px;
              position: absolute;
              top: 2px;
              inset-inline-start: -24px;
            }
            &::marker {
              content: "";
            }
          }
        `
      : ""};
  `;
};

const getListStyleType = (
  type: "ordered-list" | "unordered-list" | "with-icon",
  anyLevel: string | number
) => {
  const level = +anyLevel;

  switch (type) {
    case "ordered-list": {
      if (level === 1) {
        return "decimal";
      }
      if (level === 2) {
        return "";
      }
      return "";
    }
    case "unordered-list": {
      if (level === 1) {
        return `"-  "`;
      }
      if (level === 2) {
        return "disc";
      }
      return "";
    }
    default: {
      return "with-icon";
    }
  }
};
